import React, { useState, useEffect, useMemo } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  getStripeProducts,
  createCheckoutSession,
  createPortalSession,
  getSubscriptionStatus,
  getUserProfile,
} from "../../utils/api";
import { useNavigate } from "react-router-dom";
import ManagerSubscription from "./ManagerSubscription";

const Subscription = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [error, setError] = useState("");
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [isVisible, setIsVisible] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await getUserProfile();
        if (userRes?.data?.statusCode === 200) {
          setUser(userRes.data.data);
        }

        const statusRes = await getSubscriptionStatus();
        if (statusRes?.data?.data?.hasSubscription) {
          setSubscription(statusRes.data.data.subscription);
        }

        const productsRes = await getStripeProducts();
        // API returns { data: { statusCode, message, data: [...products] } }
        const productsData = productsRes?.data?.data || productsRes?.data || [];
        if (Array.isArray(productsData) && productsData.length > 0) {
          setProducts(productsData);
        }
      } catch (err) {
        console.error("Error fetching subscription data:", err);
        setError("Failed to load subscription information");
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const handleSubscribe = async (priceId) => {
    // Check if user is logged in
    const isAuthenticated = localStorage.getItem("token");
    if (!isAuthenticated) {
      navigate("/register");
      return;
    }

    setCheckoutLoading(true);
    setError("");

    try {
      const res = await createCheckoutSession(priceId);
      if (res?.data?.data?.url) {
        window.location.href = res.data.data.url;
      } else {
        setError("Failed to create checkout session");
      }
    } catch (err) {
      console.error("Error creating checkout session:", err);
      setError("Failed to start checkout process");
    }
    setCheckoutLoading(false);
  };

  const handleManageSubscription = async () => {
    setCheckoutLoading(true);
    setError("");

    try {
      const res = await createPortalSession();
      if (res?.data?.data?.url) {
        window.location.href = res.data.data.url;
      } else {
        setError("Failed to open subscription portal");
      }
    } catch (err) {
      console.error("Error creating portal session:", err);
      setError("Failed to open subscription management");
    }
    setCheckoutLoading(false);
  };

  const formatPrice = (amount) => {
    return (amount / 100).toFixed(0);
  };

  // Sort and structure products by tier (deduplicate by name)
  const sortedPlans = useMemo(() => {
    const tierOrder = { Basic: 1, Growth: 2, Pro: 3 };

    // Deduplicate by short name - keep the most recent one
    const uniqueProducts = {};
    products.forEach((product) => {
      const name = product.name?.replace("Appraiser Assistant ", "") || product.name;
      if (!uniqueProducts[name] || new Date(product.createdAt) > new Date(uniqueProducts[name].createdAt)) {
        uniqueProducts[name] = product;
      }
    });

    const processed = Object.values(uniqueProducts)
      .map((product) => {
        const name = product.name?.replace("Appraiser Assistant ", "") || product.name;
        const monthlyPrice = product.prices?.find((p) => p.interval === "month");
        const yearlyPrice = product.prices?.find((p) => p.interval === "year");

        return {
          ...product,
          shortName: name,
          tier: tierOrder[name] || 99,
          monthlyPrice,
          yearlyPrice,
        };
      })
      .sort((a, b) => a.tier - b.tier);

    return processed;
  }, [products]);

  // Features for each tier
  const planFeatures = {
    Basic: ["Car search functionality", "Basic comparison tools", "Standard support", "Up to 10 searches/month"],
    Growth: [
      "Everything in Basic",
      "Advanced comparison tools",
      "PDF report generation",
      "Salvage car listings",
      "Up to 50 searches/month",
    ],
    Pro: [
      "Everything in Growth",
      "Unlimited searches",
      "Market value analysis",
      "Priority customer support",
      "Advanced insights",
    ],
  };

  const planColors = {
    Basic: "from-blue-500 to-cyan-500",
    Growth: "from-violet-500 to-purple-500",
    Pro: "from-orange-500 to-amber-500",
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: "white" }} spin />} />
      </div>
    );
  }

  if (user?.role === "manager") {
    return (
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden pt-32">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <ManagerSubscription />
      </div>
    );
  }

  return (
    <Spin spinning={checkoutLoading} indicator={<LoadingOutlined style={{ fontSize: 48, color: "white" }} spin />}>
      <div className="relative min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

        <div className="relative pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            {/* Header */}
            <div
              className={`text-center mb-16 transition-all duration-700 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              {/* Badge */}
              <div
                className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6 transition-all duration-700 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
                }`}
              >
                <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm text-gray-300">
                  {subscription ? "Manage your plan" : "No credit card required to start"}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                {subscription ? "Your Subscription" : "Simple, Transparent"}
                <span className="block bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent animate-pulse-slow">
                  {subscription ? "" : "Pricing"}
                </span>
              </h1>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                {subscription
                  ? "Manage your subscription and billing information below"
                  : "Choose the plan that fits your needs. Upgrade or downgrade anytime."}
              </p>
            </div>

            {error && (
              <div className="max-w-md mx-auto mb-8 p-4 bg-red-500/20 border border-red-500/50 text-red-300 rounded-xl text-center backdrop-blur-sm">
                {error}
              </div>
            )}

            {/* Active Subscription Display */}
            {subscription && (
              <div className="max-w-md mx-auto mb-12">
                <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border-2 border-green-500/50 shadow-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center gap-2 text-sm font-medium text-green-400 bg-green-500/20 px-3 py-1 rounded-full">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                      </span>
                      Active
                    </span>
                    <span className="text-sm text-gray-400">Status: {subscription.status}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">Current Subscription</h3>
                  {subscription.currentPeriodEnd && (
                    <p className="text-gray-400 mb-6">
                      {subscription.isCodeBased
                        ? "Expires on: "
                        : subscription.cancelAtPeriodEnd
                        ? "Cancels on: "
                        : "Renews on: "}
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </p>
                  )}
                  {subscription.isCodeBased ? (
                    <div className="w-full py-4 bg-white/5 border border-white/10 text-gray-400 rounded-xl text-center">
                      <p className="text-sm">To manage your subscription, please contact admin</p>
                    </div>
                  ) : (
                    <button
                      onClick={handleManageSubscription}
                      className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all font-medium shadow-lg shadow-blue-500/25"
                    >
                      Manage Subscription
                    </button>
                  )}
                  <button
                    onClick={() => navigate("/form_page")}
                    className="w-full mt-3 py-4 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-all font-medium"
                  >
                    Continue to App
                  </button>
                </div>
              </div>
            )}

            {/* Billing Toggle */}
            {!subscription && products.length > 0 && (
              <div className="flex justify-center mb-12">
                <div className="bg-white/10 backdrop-blur-sm p-1.5 rounded-2xl inline-flex items-center border border-white/20">
                  <button
                    onClick={() => setBillingPeriod("monthly")}
                    className={`px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                      billingPeriod === "monthly"
                        ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingPeriod("yearly")}
                    className={`px-6 py-3 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                      billingPeriod === "yearly"
                        ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    Yearly
                    <span className="bg-green-500/20 text-green-400 text-xs px-2 py-0.5 rounded-full font-semibold">
                      Save 17%
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Pricing Cards */}
            {!subscription && sortedPlans.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-20">
                {sortedPlans.map((plan, index) => {
                  const currentPrice = billingPeriod === "monthly" ? plan.monthlyPrice : plan.yearlyPrice;
                  const isPopular = plan.shortName === "Growth";
                  const features = planFeatures[plan.shortName] || [];
                  const colorGradient = planColors[plan.shortName] || "from-blue-500 to-cyan-500";

                  if (!currentPrice) return null;

                  return (
                    <div
                      key={plan._id}
                      className={`relative group transition-all duration-500 ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                      } ${isPopular ? "md:-mt-4 md:mb-4" : ""}`}
                      style={{ transitionDelay: `${index * 100}ms` }}
                    >
                      {/* Popular Badge */}
                      {isPopular && (
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                          <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold px-4 py-1.5 rounded-full shadow-lg">
                            Most Popular
                          </span>
                        </div>
                      )}

                      <div
                        className={`h-full bg-white/10 backdrop-blur-xl rounded-3xl border transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${
                          isPopular
                            ? "border-cyan-500/50 shadow-lg shadow-cyan-500/20"
                            : "border-white/20 hover:border-white/40"
                        }`}
                      >
                        <div className="p-8">
                          {/* Plan Icon */}
                          <div
                            className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${colorGradient} text-white mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}
                          >
                            {plan.shortName === "Basic" && (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 10V3L4 14h7v7l9-11h-7z"
                                />
                              </svg>
                            )}
                            {plan.shortName === "Growth" && (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                                />
                              </svg>
                            )}
                            {plan.shortName === "Pro" && (
                              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                                />
                              </svg>
                            )}
                          </div>

                          {/* Plan Name */}
                          <h3 className="text-xl font-bold text-white mb-2">{plan.shortName}</h3>
                          <p className="text-gray-400 text-sm mb-6 min-h-[40px]">{plan.description}</p>

                          {/* Price */}
                          <div className="mb-6">
                            <div className="flex items-baseline">
                              <span className="text-4xl font-bold text-white">
                                ${formatPrice(currentPrice.unitAmount)}
                              </span>
                              <span className="text-gray-400 ml-2">
                                /{billingPeriod === "monthly" ? "month" : "year"}
                              </span>
                            </div>
                            {billingPeriod === "yearly" && plan.monthlyPrice && (
                              <p className="text-sm text-gray-500 mt-1">
                                ${(currentPrice.unitAmount / 12 / 100).toFixed(2)}/month billed annually
                              </p>
                            )}
                          </div>

                          {/* Subscribe Button */}
                          <button
                            onClick={() => handleSubscribe(currentPrice.stripePriceId)}
                            className={`w-full py-4 px-4 rounded-xl font-medium transition-all transform hover:-translate-y-0.5 ${
                              isPopular
                                ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                                : "bg-white/10 text-white border border-white/20 hover:bg-white/20"
                            }`}
                          >
                            Get Started
                          </button>

                          {/* Features */}
                          <div className="mt-8 pt-8 border-t border-white/10">
                            <ul className="space-y-4">
                              {features.map((feature, featureIndex) => (
                                <li key={featureIndex} className="flex items-start">
                                  <svg
                                    className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                                      isPopular ? "text-cyan-400" : "text-green-400"
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                  <span className="text-gray-300 text-sm">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* No Products Available */}
            {!subscription && products.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-12 max-w-md mx-auto border border-white/20">
                  <svg
                    className="w-16 h-16 text-gray-400 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                    />
                  </svg>
                  <p className="text-gray-300 text-lg mb-2">No subscription plans available at the moment.</p>
                  <p className="text-gray-500">Please check back later or contact support.</p>
                </div>
              </div>
            )}

            {/* Trust badges */}
            {!subscription && (
              <div
                className={`transition-all duration-700 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
              >
                {/* Trust badges */}
                <div className="flex flex-wrap justify-center gap-8 text-gray-500">
                  {[
                    {
                      icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                      text: "Secure checkout",
                    },
                    {
                      icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
                      text: "Powered by Stripe",
                    },
                    { icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z", text: "Cancel anytime" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                      </svg>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default Subscription;
